import Luminary, { type AspectRatio, VideoData } from '@lumen5/luminary';

import { getSceneDescriptions } from '../video-state/serialization';
import type { SceneDescription } from '../video-state/types';

import { TILE_WIDTH } from './scene-design-tile-strip';
import { renderSceneToDataUrl } from './scene-render-helpers';

import { getTemplateBundle } from 'js/src/data/templates/templates-selectors';
import { sampleTranscripts } from 'js/src/editor/sample-data';
import { FullscreenSceneSelectorLuminaryConfigOverrides } from 'js/src/libs/consts';
import type { RootState } from 'js/src/libs/RootState';
import { DEFAULT_LUMINARY_CONFIG } from 'js/src/libs/video-data-consts';

/**
 * Thrown when the requested template's bundle isn't in the Redux cache, so there
 * is nothing to render offscreen. The tool converts this into a clear text
 * result rather than attempting a server-side load.
 */
export class TemplateBundleNotLoadedError extends Error {
    public constructor(templateId: string) {
        super(`Template bundle for "${templateId}" is not loaded`);
        this.name = 'TemplateBundleNotLoadedError';
    }
}

export interface SceneDesignRenderResult {
    sceneDesignId: string;
    dataUrl: string | null;
    /** The instantiated design's asset slots, present only when `describe` is requested. */
    description?: SceneDescription;
}

const DEFAULT_PER_RENDER_TIMEOUT_MS = 15000;

/** Designs render exactly one tile-strip panel wide, so a saved poster drops into a strip unscaled. */
export const SCENE_DESIGN_POSTER_WIDTH = TILE_WIDTH;

/** The Luminary `resolutionValue` that makes a frame of `aspectRatio` exactly poster-wide. */
export function getSceneDesignPosterResolutionValue(aspectRatio: AspectRatio): number {
    const referenceResolutionValue = 1080;
    const { width } = VideoData.getResolution(referenceResolutionValue, aspectRatio);
    return Math.round((referenceResolutionValue * SCENE_DESIGN_POSTER_WIDTH) / width);
}

/**
 * Render a set of scene designs (from a single, already-loaded template) to
 * poster-sized PNG data URLs using one throwaway offscreen Luminary.
 * Renders are sequential because they share a single render queue. A design that
 * times out or fails to render comes back with `dataUrl: null` so callers keep a
 * complete list.
 */
export async function renderSceneDesignsToDataUrls({
    templateId,
    aspectRatio,
    sceneDesignIds,
    getState,
    perRenderTimeoutMs = DEFAULT_PER_RENDER_TIMEOUT_MS,
    describe = false,
}: {
    templateId: string;
    aspectRatio: AspectRatio;
    sceneDesignIds: string[];
    getState: () => RootState;
    perRenderTimeoutMs?: number;
    /**
     * When true, also capture each design's structural description from the scene.
     */
    describe?: boolean;
}): Promise<SceneDesignRenderResult[]> {
    const bundle = getTemplateBundle(getState(), templateId);
    if (!bundle) {
        throw new TemplateBundleNotLoadedError(templateId);
    }

    const luminary = new Luminary({
        ...DEFAULT_LUMINARY_CONFIG,
        ...FullscreenSceneSelectorLuminaryConfigOverrides(),
        resolutionValue: getSceneDesignPosterResolutionValue(aspectRatio),
    });

    const results: SceneDesignRenderResult[] = [];
    try {
        luminary.setTemplate(bundle);
        luminary.aspectRatio = aspectRatio;
        luminary.template.transitionsEnabled = false;

        // Seed a placeholder transcript on every scene, same as the scene design
        // editor/picker (loadSceneDesignsDataIntoLuminary) — otherwise animated
        // caption designs have no transcript to render captions from and preview blank.
        const scenes = luminary.batchRun((safeLuminary) =>
            sceneDesignIds.map((sceneDesignId) => {
                const scene = safeLuminary.addScene({ sceneDesignId });
                scene.sceneGroup!.originalTranscript.transcriptData =
                    sampleTranscripts[1].transcriptData;
                return { sceneDesignId, scene };
            }),
        );

        await luminary.onAllSlideImagesRendered();

        for (const { sceneDesignId, scene } of scenes) {
            const dataUrl = await renderSceneToDataUrl(luminary, scene, {
                subsceneIndex: 0,
                timeoutMs: perRenderTimeoutMs,
            });
            const description = describe ? getSceneDescriptions(scene, luminary) : undefined;
            results.push({ sceneDesignId, dataUrl, description });
        }
    } finally {
        luminary.destroy();
    }

    return results;
}
